import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Paper,
    Snackbar,
    Alert,
    MenuItem,
    TextField,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { createProductTab, updateProductTab, getProductTabById } from "../../Apis/ProductTabsApi";

const CreateProductTab = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const productIdFromQuery = searchParams.get("productId") || "";

    const [productId, setProductId] = useState(productIdFromQuery);
    const [tabTitle, setTabTitle] = useState("What You Receive");
    const [tabContent, setTabContent] = useState("");
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const editMode = Boolean(id);

    useEffect(() => {
        if (editMode) {
            getProductTabById(id)
                .then((res) => {
                    const tab = res?.data || res;
                    setProductId(tab.productId || "");
                    setTabTitle(tab.tabTitle || "What You Receive");
                    setTabContent(tab.tabContent || "");
                    setPreview(tab.producttabImageUrl || null);
                })
                .catch(() => setErrorMsg("Failed to load product tab."));
        }
    }, [editMode, id]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setImage(file);
        if (file) setPreview(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!productId || !tabTitle || !tabContent) {
            setErrorMsg("Please fill all required fields.");
            return;
        }

        const formData = new FormData();
        formData.append("productId", productId);
        formData.append("tabTitle", tabTitle);
        formData.append("tabContent", tabContent);
        if (image) {
            formData.append("producttabImage", image);
            console.log("Image added:", image.name);
        }

        // Debugging: Log all form data entries
        for (let pair of formData.entries()) {
            console.log(pair[0], pair[1]);
        }

        try {
            setLoading(true);
            if (editMode) {
                await updateProductTab(id, formData);
            } else {
                await createProductTab(formData);
            }
            setSuccess(true);
            setTimeout(() => navigate(`/getproducttabs/${productId}`), 1200);
        } catch (error) {
            console.error("Error updating tab:", error.response ? error.response.data : error.message);
            setErrorMsg(error.response?.data?.message || (editMode ? "Failed to update tab." : "Failed to create tab."));
        } finally {
            setLoading(false);
        }
    };


    return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center"}}>
            <Paper sx={{ width: "45%", p: 5, borderRadius: 3, boxShadow: 6 }}>
                <Typography variant="h5" gutterBottom>
                    {editMode ? "Update Product Tab" : "Create Product Tab"}
                </Typography>

                <form onSubmit={handleSubmit}>
                    {/* Tab Title Dropdown with only "What You Receive" */}
                    <TextField
                        label="Tab Title"
                        value={tabTitle}
                        onChange={(e) => setTabTitle(e.target.value)}
                        fullWidth
                        margin="normal"
                        select
                    >
                        <MenuItem value="What You Receive">What You Receive</MenuItem>
                        <MenuItem value="You Can Trust">You Can Trust</MenuItem>
                    </TextField>

                    {/* Tab Content with ReactQuill */}
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Tab Content</Typography>
                    <ReactQuill
                        theme="snow"
                        value={tabContent}
                        onChange={setTabContent}
                        style={{ height: 200, marginBottom: 20 }}
                    />

                    {/* Image Upload */}
                    <Button
                        variant="contained"
                        component="label"
                        sx={{ mt: 5, mb: 2 }}
                        fullWidth
                    >
                        Upload Image
                        <input type="file" hidden onChange={handleImageChange} />
                    </Button>
                    {preview && (
                        <Box sx={{ mb: 2, textAlign: "center" }}>
                            <Typography variant="body2">Preview:</Typography>
                            <img
                                src={preview}
                                alt="preview"
                                style={{ width: 120, marginTop: 8, borderRadius: 8 }}
                            />
                        </Box>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={loading}
                     
                    >
                        {loading ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update Product Tab" : "Create Product Tab")}
                    </Button>
                </form>
            </Paper>

            <Snackbar
                open={success}
                autoHideDuration={3000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert severity="success" variant="filled">
                    {editMode ? "Product tab updated successfully!" : "Product tab created successfully!"}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!errorMsg}
                autoHideDuration={4000}
                onClose={() => setErrorMsg("")}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert severity="error" variant="filled">{errorMsg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default CreateProductTab;
